import { useEffect } from 'react'
import { t } from '@lingui/core/macro'
import { useDispatch } from 'react-redux'
import { Switch, Route, useHistory, useLocation, Redirect } from 'react-router-dom'
import { ProgressiveImage } from '@components/progressiveImage'
import { Button } from '@components/v2/button'
import { useAppSelector } from '@redux/hooks'
import ClaravineLogo from 'images/claravine-logo.svg'
import { ConfirmPasswordReset } from './confirm-password-reset'
import { Login } from './login'
import { useAuthHintQuery, useSsoEnabled } from '@global/hooks/queries/useAuthHintQuery'
import { Mfa } from './mfa'
import { useMfaStore } from './mfa/store/useMfaStore'
import { ResetPassword } from './reset-password'
import * as css from './style'
import { Welcome } from './welcome'
import { LoadingPage } from '../global/loading'
import * as SnackbarActions from '../global/snackbar/snackbar.actions'
import { trackEvent, EventTrackingEventType } from 'lib/eventTracking'

const CTA_URL = 'https://www.claravine.com/connect-claravine-snowflake'

export const Auth = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const bootstrapComplete = useAppSelector(state => state.Auth.bootstrapComplete)
  const { isInitialLoading: isAuthHintLoading } = useAuthHintQuery()
  const queryParams = new URLSearchParams(location.search)
  const forceRegularLogin = queryParams.get('login') === 'system'
  const loggedIn = !!useAppSelector(state => state.Auth.user)
  const { isMfaRequired, isMfaSuccessful } = useMfaStore()
  const hasAccountSsoEnabled = useSsoEnabled()

  // first load check for query params
  useEffect(() => {
    if (location.search.includes('securityLoggedOut')) {
      dispatch(
        SnackbarActions.showSnackbar({
          message: t`Due to inactivity—and for your protection—you were logged out.`,
          type: 'INFO',
        }),
      )
      history.replace('/auth/login')
    }
  }, [history, dispatch, location])

  if (loggedIn) {
    const postLoginDestination = location?.state?.referrer || { pathname: '/' }
    return <Redirect to={`${postLoginDestination.pathname}${location?.search}`} />
  }

  /**
   * If MFA is required and successful, and the user isn't logged in,
   * it means we are in the process of logging in with MFA
   */
  if (isMfaRequired && isMfaSuccessful) {
    return <LoadingPage />
  }

  const showLoginPage =
    bootstrapComplete &&
    !isAuthHintLoading &&
    (forceRegularLogin || !hasAccountSsoEnabled || isMfaRequired)

  if (!showLoginPage) {
    return <LoadingPage />
  }

  return (
    <css.LoginWrapper>
      <css.Modal
        className="rounded-[20px] shadow-clvn-lg"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, transition: { duration: 1 } }}
      >
        <css.ModalForm>
          <ClaravineLogo className="claravine-logo max-w-[180px] mb-[40px]" />
          <Switch>
            <Route path="/auth/login">
              <Login />
            </Route>
            <Route path="/auth/forgot">
              <ResetPassword />
            </Route>
            <Route path="/auth/reset-password/:token">
              <ConfirmPasswordReset />
            </Route>
            <Route path="/auth/welcome/:token">
              <Welcome />
            </Route>
            <Route path="/auth/mfa">
              <Mfa />
            </Route>
          </Switch>
        </css.ModalForm>
        <css.ModalPreview className="rounded-[20px]">
          <css.PreviewBox
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 0.5, duration: 2 } }}
          >
            <ProgressiveImage
              className="absolute top-0 left-0 right-0 bottom-0"
              imgClassName="h-full w-full object-cover"
              src="/images/clvn-snwflk-bg.webp"
              alt={t`Description`}
              placeholderSrc="/images/clvn-snwflk-bg.png"
            />
            <css.PreviewContent>
              <h1 className="antialiased font-sans font-bold text-[24px] mb-[5px]">
                {t`You've Standardized Your Data.`}
                <br />
                {t`Now Supercharge It.`}
              </h1>
              <p className="antialiased font-sans font-normal text-xs text-[#DEF7FF] leading-[1.65]">
                {t`With Snowflake Data Sharing, you can instantly access governed marketing data from Claravine across teams, tools, and partners — no pipelines or engineering required. Structured data flows directly into your Snowflake environment, ready for activation, analysis, and scale.`}
              </p>
            </css.PreviewContent>
            <Button
              className="z-[1] mt-[20px] antialiased !text-[14px] bg-cv-green-600 border-[#61C296] shadow-clvn-login"
              variant="primary"
              size="lg"
              onClick={() => {
                trackEvent(EventTrackingEventType.LOGIN_MODAL_PREVIEW, {
                  name: 'AutoSubmit',
                  url: CTA_URL,
                })
                window.open(CTA_URL, '_blank', 'noopener')
              }}
            >
              {t`Learn More`}
            </Button>
          </css.PreviewBox>
        </css.ModalPreview>
      </css.Modal>
    </css.LoginWrapper>
  )
}
